import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isManager, isAdmin } from '../utils/role';
import Avatar from '../components/Avatar';
import GanttChart from '../components/GanttChart';

const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'BLOCKED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const MS_STATUSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];

const statusColor = (s) => ({ TODO: 'badge-gray', IN_PROGRESS: 'badge-info', IN_REVIEW: 'badge-warning', DONE: 'badge-success', BLOCKED: 'badge-danger', PENDING: 'badge-gray', COMPLETED: 'badge-success', OVERDUE: 'badge-danger' }[s] || 'badge-gray');
const priorityColor = (p) => ({ CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' }[p] || 'badge-gray');
const colColor = { TODO: '#6b7280', IN_PROGRESS: '#1a56db', IN_REVIEW: '#d97706', DONE: '#059669', BLOCKED: '#dc2626' };
const colLabel = { TODO: 'To Do', IN_PROGRESS: 'In Progress', IN_REVIEW: 'In Review', DONE: 'Done', BLOCKED: 'Blocked' };

const emptyTaskForm = { title: '', description: '', assigneeId: '', assigneeUsername: '', assigneeName: '', milestoneId: '', status: 'TODO', priority: 'MEDIUM', startDate: '', dueDate: '', estimatedHours: '' };
const MEMBER_ROLES = ['DEVELOPER', 'TEAM_LEAD', 'MEMBER', 'TESTER', 'DESIGNER'];
const emptyMsForm = { milestoneName: '', startDate: '', dueDate: '', status: 'PENDING', description: '' };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('kanban');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showMsForm, setShowMsForm] = useState(false);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [msForm, setMsForm] = useState(emptyMsForm);
  const [editingTask, setEditingTask] = useState(null);
  const [editingMs, setEditingMs] = useState(null);
  const [dragTask, setDragTask] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [taskFilter, setTaskFilter] = useState({ assignee: '', priority: '', milestone: '' });
  const [projectMembers, setProjectMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ userId: '', teamId: '', roleInProject: 'DEVELOPER' });
  const [subtasks, setSubtasks] = useState({});
  const [expandedTask, setExpandedTask] = useState(null);
  const [showSubtaskForm, setShowSubtaskForm] = useState(null);
  const [subtaskForm, setSubtaskForm] = useState({ title: '', assigneeId: '', assigneeUsername: '', assigneeName: '', priority: 'MEDIUM' });
  const [parentTaskId, setParentTaskId] = useState(null);
  const [msAttachments, setMsAttachments] = useState({});
  const [msUploading, setMsUploading] = useState(null);
  const msFileRef = React.useRef(null);
  const [projectAttachments, setProjectAttachments] = useState([]);

  const loadMsAttachments = async (msId) => {
    try {
      const r = await api.get(`/attachments?entityType=MILESTONE&entityId=${msId}`);
      setMsAttachments(prev => ({ ...prev, [msId]: r.data }));
    } catch {}
  };

  const uploadMsFile = async (msId, file) => {
    if (!file) return;
    setMsUploading(msId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'MILESTONE');
      formData.append('entityId', msId);
      formData.append('projectId', id);
      await api.post('/attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadMsAttachments(msId);
    } catch (err) { alert(err.response?.data?.error || 'Upload failed'); }
    finally { setMsUploading(null); if (msFileRef.current) msFileRef.current.value = ''; }
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`);
      window.open(r.data.url, '_blank');
    } catch { alert('Download failed'); }
  };

  const deleteAttachment = async (att, msId) => {
    if (!window.confirm(`Delete "${att.fileName}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      if (msId) loadMsAttachments(msId);
    } catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','svg','webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📕';
    if (['doc','docx'].includes(ext)) return '📘';
    if (['xls','xlsx','csv'].includes(ext)) return '📗';
    if (['zip','rar','7z','tar','gz'].includes(ext)) return '📦';
    return '📄';
  };

  const load = () => {
    api.get(`/projects/${id}`).then(r => setProject(r.data)).catch(() => navigate('/projects'));
    api.get(`/projects/${id}/tasks`).then(r => setTasks(r.data)).catch(() => {});
    api.get(`/projects/${id}/milestones`).then(r => setMilestones(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get(`/projects/${id}/members`).then(r => setProjectMembers(r.data)).catch(() => {});
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const openNewTask = (status = 'TODO') => {
    setTaskForm({ ...emptyTaskForm, status });
    setEditingTask(null); setShowTaskForm(true);
  };

  const openEditTask = (t) => {
    setTaskForm({ title: t.title, description: t.description || '', assigneeId: t.assigneeId || '', assigneeUsername: t.assigneeUsername || '', assigneeName: t.assigneeName || '', milestoneId: t.milestoneId || '', status: t.status, priority: t.priority, startDate: t.startDate || '', dueDate: t.dueDate || '', estimatedHours: t.estimatedHours || '' });
    setEditingTask(t.id); setShowTaskForm(true);
  };

  const saveTask = async () => {
    if (!taskForm.title) return;
    const payload = { ...taskForm, projectId: id, milestoneId: taskForm.milestoneId || null, assigneeId: taskForm.assigneeId || null };
    if (editingTask) {
      await api.put(`/tasks/${editingTask}`, payload);
    } else {
      await api.post(`/projects/${id}/tasks`, payload);
    }
    setShowTaskForm(false); setEditingTask(null); setTaskForm(emptyTaskForm); load();
  };

  const addProjectMember = async () => {
    if (!addMemberForm.userId) return;
    const user = users.find(u => u.id === Number(addMemberForm.userId));
    const team = teams.find(t => t.id === Number(addMemberForm.teamId));
    if (!user) return;
    try {
      await api.post(`/projects/${id}/members`, {
        userId: user.id, username: user.username, fullName: user.fullName,
        teamId: team ? team.id : null, teamName: team ? team.teamName : '',
        roleInProject: addMemberForm.roleInProject
      });
      setShowAddMember(false); setAddMemberForm({ userId: '', teamId: '', roleInProject: 'DEVELOPER' }); load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to add member'); }
  };

  const removeProjectMember = async (memberId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await api.delete(`/projects/${id}/members/${memberId}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to remove'); }
  };

  const activeProjectMembers = projectMembers.filter(m => m.status === 'ACTIVE');
  const existingMemberUserIds = activeProjectMembers.map(m => m.userId);

  const loadSubtasks = async (taskId) => {
    try {
      const r = await api.get(`/tasks/${taskId}/subtasks`);
      setSubtasks(prev => ({ ...prev, [taskId]: r.data }));
    } catch {}
  };

  const toggleTaskExpand = (taskId) => {
    if (expandedTask === taskId) { setExpandedTask(null); }
    else { setExpandedTask(taskId); loadSubtasks(taskId); }
  };

  const saveSubtask = async (parentId) => {
    if (!subtaskForm.title) return;
    await api.post(`/tasks/${parentId}/subtasks`, subtaskForm);
    setShowSubtaskForm(null); setSubtaskForm({ title: '', assigneeId: '', assigneeUsername: '', assigneeName: '', priority: 'MEDIUM' });
    loadSubtasks(parentId); load();
  };

  const updateSubtaskStatus = async (subtask, newStatus) => {
    await api.patch(`/tasks/${subtask.id}/status`, { status: newStatus });
    loadSubtasks(subtask.parentTaskId); load();
  };

  const deleteSubtask = async (subtask) => {
    if (!window.confirm('Delete this subtask?')) return;
    await api.delete(`/tasks/${subtask.id}`);
    loadSubtasks(subtask.parentTaskId); load();
  };

  const getSubtasks = (taskId) => subtasks[taskId] || [];
  const getSubtaskProgress = (taskId) => {
    const subs = tasks.filter(t => t.parentTaskId === taskId);
    if (subs.length === 0) return null;
    const done = subs.filter(t => t.status === 'DONE').length;
    return { total: subs.length, done, pct: Math.round((done / subs.length) * 100) };
  };

  const parentTasks = tasks.filter(t => !t.parentTaskId);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  };

  const saveMs = async () => {
    if (!msForm.milestoneName || !msForm.dueDate) return;
    if (editingMs) {
      await api.put(`/projects/${id}/milestones/${editingMs}`, msForm);
    } else {
      await api.post(`/projects/${id}/milestones`, msForm);
    }
    setShowMsForm(false); setEditingMs(null); setMsForm(emptyMsForm); load();
  };

  const deleteMs = async (msId) => {
    if (!window.confirm('Delete this milestone? Tasks linked to it will be unlinked.')) return;
    try {
      await api.delete(`/projects/${id}/milestones/${msId}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  };

  // Drag and drop
  const handleDragStart = (e, task) => {
    setDragTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const handleDragOver = (e, col) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col);
  };

  const handleDrop = async (e, col) => {
    e.preventDefault();
    setDragOverCol(null);
    if (dragTask && dragTask.status !== col) {
      await api.patch(`/tasks/${dragTask.id}/status`, { status: col });
      load();
    }
    setDragTask(null);
  };

  const handleDragEnd = () => { setDragTask(null); setDragOverCol(null); };

  // Filtered tasks
  const filteredTasks = parentTasks.filter(t => {
    if (taskFilter.assignee && String(t.assigneeId) !== taskFilter.assignee) return false;
    if (taskFilter.priority && t.priority !== taskFilter.priority) return false;
    if (taskFilter.milestone && String(t.milestoneId) !== taskFilter.milestone) return false;
    return true;
  });

  // Milestone stats
  const getMsStats = (msId) => {
    const msTasks = tasks.filter(t => t.milestoneId === msId);
    const done = msTasks.filter(t => t.status === 'DONE').length;
    return { total: msTasks.length, done, pct: msTasks.length > 0 ? Math.round((done / msTasks.length) * 100) : 0 };
  };

  const getMsName = (msId) => {
    const m = milestones.find(m => m.id === msId);
    return m ? m.milestoneName : '';
  };

  const assignees = activeProjectMembers.length > 0
    ? activeProjectMembers
    : [...new Set(tasks.filter(t => t.assigneeId).map(t => ({ userId: t.assigneeId, fullName: t.assigneeName })))];

  if (!project) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  return (
    <div>
      <button className="btn-ghost" onClick={() => navigate('/projects')} style={{ marginBottom: 16, fontSize: 13 }}>← Back to Projects</button>

      {/* Project Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{project.projectCode}</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{project.projectName}</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span className={`badge ${statusColor(project.status)}`}>{project.status}</span>
              <span className={`badge ${priorityColor(project.priority)}`}>{project.priority}</span>
              {project.manager && <span className="badge badge-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Avatar name={project.manager} size={16} fontSize={7} /> {project.manager}</span>}
              {project.department && <span className="badge badge-gray">🏢 {project.department}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
            <div>{project.startDate} → {project.endDate}</div>
            {project.budget && <div style={{ fontWeight: 600, marginTop: 4 }}>Budget: ₹{Number(project.budget).toLocaleString()}</div>}
          </div>
        </div>
        {project.description && <p style={{ marginTop: 12, color: '#4b5563', fontSize: 14 }}>{project.description}</p>}

        {/* Quick Stats */}
        <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
          {[
            { label: 'Tasks', value: tasks.length, color: '#1a56db' },
            { label: 'Done', value: tasks.filter(t => t.status === 'DONE').length, color: '#059669' },
            { label: 'In Progress', value: tasks.filter(t => t.status === 'IN_PROGRESS').length, color: '#d97706' },
            { label: 'Blocked', value: tasks.filter(t => t.status === 'BLOCKED').length, color: '#dc2626' },
            { label: 'Milestones', value: milestones.length, color: '#8b5cf6' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</span>
            </div>
          ))}
          {tasks.length > 0 && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 120, height: 6, background: '#e5e7eb', borderRadius: 3 }}>
                <div style={{ height: '100%', width: `${Math.round((tasks.filter(t => t.status === 'DONE').length / tasks.length) * 100)}%`, background: '#059669', borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>{Math.round((tasks.filter(t => t.status === 'DONE').length / tasks.length) * 100)}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
        {['kanban', 'tasks', 'milestones', 'timeline', 'documents', 'members'].map(t => (
          <button key={t} onClick={() => {
            setTab(t);
            if (t === 'milestones') milestones.forEach(m => loadMsAttachments(m.id));
            if (t === 'documents') api.get(`/attachments/project/${id}`).then(r => setProjectAttachments(r.data)).catch(() => {});
          }} style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 600, borderRadius: '8px 8px 0 0', border: 'none',
            background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#1a56db' : '#6b7280',
            borderBottom: tab === t ? '2px solid #1a56db' : '2px solid transparent', cursor: 'pointer'
          }}>{t === 'kanban' ? '📊 Kanban' : t === 'tasks' ? '📋 Tasks' : t === 'milestones' ? '🎯 Milestones' : t === 'timeline' ? '📅 Timeline' : t === 'documents' ? '📎 Documents' : `👥 Members (${activeProjectMembers.length})`}</button>
        ))}
      </div>

      {/* Task Form Modal */}
      {showTaskForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#0f172a' }}>{editingTask ? 'Edit Task' : 'New Task'}</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Title *</label>
                <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Task title" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Assignee</label>
                  <select value={taskForm.assigneeId} onChange={e => {
                    const uid = e.target.value;
                    if (!uid) { setTaskForm({ ...taskForm, assigneeId: '', assigneeUsername: '', assigneeName: '' }); return; }
                    const member = activeProjectMembers.find(m => m.userId === Number(uid));
                    if (member) { setTaskForm({ ...taskForm, assigneeId: member.userId, assigneeUsername: member.username, assigneeName: member.fullName }); }
                    else {
                      const u = users.find(u => u.id === Number(uid));
                      if (u) setTaskForm({ ...taskForm, assigneeId: u.id, assigneeUsername: u.username, assigneeName: u.fullName });
                    }
                  }}>
                    <option value="">Unassigned</option>
                    {activeProjectMembers.length > 0
                      ? activeProjectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName} ({m.roleInProject})</option>)
                      : users.filter(u => u.active).map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)
                    }
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Milestone</label>
                  <select value={taskForm.milestoneId} onChange={e => setTaskForm({ ...taskForm, milestoneId: e.target.value })}>
                    <option value="">No Milestone</option>
                    {milestones.map(m => <option key={m.id} value={m.id}>{m.milestoneName}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Status</label>
                  <select value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value })}>
                    {TASK_STATUSES.map(s => <option key={s} value={s}>{colLabel[s]}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Priority</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Start Date</label>
                  <input type="date" value={taskForm.startDate} onChange={e => setTaskForm({ ...taskForm, startDate: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Due Date</label>
                  <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Est. Hours</label>
                  <input type="number" value={taskForm.estimatedHours} onChange={e => setTaskForm({ ...taskForm, estimatedHours: e.target.value })} placeholder="0" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Description</label>
                <textarea rows={3} value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Details..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn-primary" onClick={saveTask} style={{ flex: 1 }}>{editingTask ? 'Update' : 'Create'}</button>
              {editingTask && (isAdmin() || (() => { const t = tasks.find(t => t.id === editingTask); return t && t.createdBy === currentUser.username; })()) && (
                <button className="btn-danger" onClick={() => { deleteTask(editingTask); setShowTaskForm(false); }} style={{ padding: '10px 20px' }}>Delete</button>
              )}
              <button className="btn-ghost" onClick={() => { setShowTaskForm(false); setEditingTask(null); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Milestone Form Modal */}
      {showMsForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#0f172a' }}>{editingMs ? 'Edit Milestone' : 'New Milestone'}</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Name *</label>
                <input value={msForm.milestoneName} onChange={e => setMsForm({ ...msForm, milestoneName: e.target.value })} placeholder="Milestone name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Start Date</label>
                  <input type="date" value={msForm.startDate} onChange={e => setMsForm({ ...msForm, startDate: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Due Date *</label>
                  <input type="date" value={msForm.dueDate} onChange={e => setMsForm({ ...msForm, dueDate: e.target.value })} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Status</label>
                <select value={msForm.status} onChange={e => setMsForm({ ...msForm, status: e.target.value })}>
                  {MS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Description</label>
                <textarea rows={3} value={msForm.description} onChange={e => setMsForm({ ...msForm, description: e.target.value })} placeholder="Details..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn-primary" onClick={saveMs} style={{ flex: 1 }}>{editingMs ? 'Update' : 'Create'}</button>
              {editingMs && (isAdmin() || (() => { const m = milestones.find(m => m.id === editingMs); return m && m.createdBy === currentUser.username; })()) && (
                <button className="btn-danger" onClick={() => { deleteMs(editingMs); setShowMsForm(false); setEditingMs(null); }} style={{ padding: '10px 20px' }}>Delete</button>
              )}
              <button className="btn-ghost" onClick={() => { setShowMsForm(false); setEditingMs(null); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* KANBAN TAB */}
      {tab === 'kanban' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
            {isManager() && <button className="btn-primary" onClick={() => openNewTask()} style={{ fontSize: 13 }}>+ Add Task</button>}
            <select value={taskFilter.assignee} onChange={e => setTaskFilter({ ...taskFilter, assignee: e.target.value })} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, width: 150 }}>
              <option value="">All Assignees</option>
              {activeProjectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName}</option>)}
            </select>
            <select value={taskFilter.priority} onChange={e => setTaskFilter({ ...taskFilter, priority: e.target.value })} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, width: 130 }}>
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={taskFilter.milestone} onChange={e => setTaskFilter({ ...taskFilter, milestone: e.target.value })} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, width: 150 }}>
              <option value="">All Milestones</option>
              {milestones.map(m => <option key={m.id} value={m.id}>{m.milestoneName}</option>)}
            </select>
            {(taskFilter.assignee || taskFilter.priority || taskFilter.milestone) && (
              <button className="btn-ghost" onClick={() => setTaskFilter({ assignee: '', priority: '', milestone: '' })} style={{ fontSize: 12, padding: '6px 10px' }}>Clear</button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            {TASK_STATUSES.map(col => (
              <div key={col}
                onDragOver={(e) => handleDragOver(e, col)}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={(e) => handleDrop(e, col)}
                style={{
                  background: dragOverCol === col ? '#eef2ff' : '#f9fafb',
                  borderRadius: 12, padding: 12, minHeight: 300,
                  border: dragOverCol === col ? '2px dashed #3b82f6' : '2px solid transparent',
                  transition: 'all 0.15s'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: `3px solid ${colColor[col]}` }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: colColor[col] }}>{colLabel[col]}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', background: '#e5e7eb', borderRadius: 10, padding: '2px 8px' }}>
                    {filteredTasks.filter(t => t.status === col).length}
                  </span>
                </div>
                {filteredTasks.filter(t => t.status === col).map(t => {
                  const subProg = getSubtaskProgress(t.id);
                  const subs = getSubtasks(t.id);
                  const isExpanded = expandedTask === t.id;
                  return (
                  <div key={t.id} className="card" style={{ padding: 0, marginBottom: 8, borderLeft: `3px solid ${colColor[t.status]}`, opacity: dragTask?.id === t.id ? 0.4 : 1 }}>
                    <div draggable onDragStart={(e) => handleDragStart(e, t)} onDragEnd={handleDragEnd}
                      style={{ padding: 12, cursor: 'grab' }} onClick={() => navigate(`/tasks/${t.id}`)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>{t.taskCode}</span>
                        <span className={`badge ${priorityColor(t.priority)}`} style={{ fontSize: 10 }}>{t.priority}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#0f172a', lineHeight: 1.3 }}>{t.title}</div>
                      {t.milestoneId && (
                        <div style={{ fontSize: 10, color: '#8b5cf6', marginBottom: 4, fontWeight: 500 }}>🎯 {getMsName(t.milestoneId)}</div>
                      )}
                      {subProg && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <div style={{ flex: 1, height: 4, background: '#e5e7eb', borderRadius: 2 }}>
                              <div style={{ height: '100%', width: `${subProg.pct}%`, background: subProg.pct === 100 ? '#059669' : '#3b82f6', borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>{subProg.done}/{subProg.total}</span>
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {t.assigneeName ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#374151', fontWeight: 500 }}>
                            <Avatar name={t.assigneeName} size={20} fontSize={9} /> {t.assigneeName}
                          </span>
                        ) : <span />}
                        {t.dueDate && (
                          <span style={{ fontSize: 10, color: new Date(t.dueDate) < new Date() && t.status !== 'DONE' ? '#dc2626' : '#9ca3af', fontWeight: 500 }}>
                            📅 {t.dueDate}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Subtask toggle */}
                    <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleTaskExpand(t.id); }} style={{ fontSize: 10, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 500 }}>
                        {isExpanded ? '▾' : '▸'} Subtasks {subProg ? `(${subProg.done}/${subProg.total})` : '(0)'}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setShowSubtaskForm(t.id); setExpandedTask(t.id); loadSubtasks(t.id); }} style={{ fontSize: 10, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 600 }}>+</button>
                    </div>
                    {/* Expanded subtasks */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px', background: '#f9fafb' }}>
                        {showSubtaskForm === t.id && (
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                            <input value={subtaskForm.title} onChange={e => setSubtaskForm({ ...subtaskForm, title: e.target.value })} placeholder="Subtask title" style={{ flex: 1, fontSize: 11, padding: '4px 8px' }} onKeyDown={e => e.key === 'Enter' && saveSubtask(t.id)} onClick={e => e.stopPropagation()} />
                            <select value={subtaskForm.assigneeId} onChange={e => {
                              const uid = e.target.value;
                              if (!uid) { setSubtaskForm({ ...subtaskForm, assigneeId: '', assigneeUsername: '', assigneeName: '' }); return; }
                              const m = activeProjectMembers.find(m => m.userId === Number(uid));
                              if (m) setSubtaskForm({ ...subtaskForm, assigneeId: m.userId, assigneeUsername: m.username, assigneeName: m.fullName });
                            }} style={{ fontSize: 10, padding: '4px', width: 80 }} onClick={e => e.stopPropagation()}>
                              <option value="">—</option>
                              {activeProjectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName}</option>)}
                            </select>
                            <button onClick={(e) => { e.stopPropagation(); saveSubtask(t.id); }} style={{ fontSize: 10, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}>Add</button>
                            <button onClick={(e) => { e.stopPropagation(); setShowSubtaskForm(null); }} style={{ fontSize: 10, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          </div>
                        )}
                        {subs.length === 0 && !showSubtaskForm && <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center', padding: 4 }}>No subtasks</div>}
                        {subs.map(s => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 11 }} onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={s.status === 'DONE'} onChange={() => updateSubtaskStatus(s, s.status === 'DONE' ? 'TODO' : 'DONE')} style={{ cursor: 'pointer', accentColor: '#059669' }} />
                            <span style={{ flex: 1, color: s.status === 'DONE' ? '#9ca3af' : '#374151', textDecoration: s.status === 'DONE' ? 'line-through' : 'none' }}>{s.title}</span>
                            {s.assigneeName && <Avatar name={s.assigneeName} size={16} fontSize={7} />}
                            {(isAdmin() || s.createdBy === currentUser.username) && <button onClick={() => deleteSubtask(s)} style={{ fontSize: 9, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
                {isManager() && (
                  <button onClick={() => openNewTask(col)} style={{
                    width: '100%', padding: '8px', fontSize: 12, color: '#9ca3af', background: 'transparent',
                    border: '1px dashed #d1d5db', borderRadius: 8, cursor: 'pointer', marginTop: 4
                  }}>+ Add</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TASKS TAB */}
      {tab === 'tasks' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Tasks ({filteredTasks.length})</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={taskFilter.assignee} onChange={e => setTaskFilter({ ...taskFilter, assignee: e.target.value })} style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6 }}>
                <option value="">All Assignees</option>
                {activeProjectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName}</option>)}
              </select>
              <select value={taskFilter.priority} onChange={e => setTaskFilter({ ...taskFilter, priority: e.target.value })} style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6 }}>
                <option value="">All Priorities</option>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {isManager() && <button className="btn-primary" onClick={() => openNewTask()} style={{ fontSize: 13 }}>+ Add Task</button>}
            </div>
          </div>
          {filteredTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>No tasks found</div>
          ) : (
            <table>
              <thead><tr><th>Code</th><th>Title</th><th>Assignee</th><th>Subtasks</th><th>Status</th><th>Priority</th><th>Due</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredTasks.map(t => {
                  const subProg = getSubtaskProgress(t.id);
                  const childTasks = tasks.filter(s => s.parentTaskId === t.id);
                  return (
                  <React.Fragment key={t.id}>
                    <tr style={{ borderLeft: `3px solid ${colColor[t.status]}` }}>
                      <td style={{ fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{t.taskCode}</td>
                      <td style={{ fontWeight: 500 }}>{t.title}</td>
                      <td>{t.assigneeName ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}><Avatar name={t.assigneeName} size={22} fontSize={10} /> {t.assigneeName}</span> : <span style={{ color: '#ccc' }}>—</span>}</td>
                      <td>{subProg ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                          <span style={{ width: 40, height: 4, background: '#e5e7eb', borderRadius: 2, display: 'inline-block' }}>
                            <span style={{ display: 'block', height: '100%', width: `${subProg.pct}%`, background: subProg.pct === 100 ? '#059669' : '#3b82f6', borderRadius: 2 }} />
                          </span>
                          <span style={{ color: '#6b7280', fontWeight: 600 }}>{subProg.done}/{subProg.total}</span>
                        </span>
                      ) : <span style={{ color: '#ccc' }}>—</span>}</td>
                      <td><span className={`badge ${statusColor(t.status)}`}>{t.status.replace(/_/g, ' ')}</span></td>
                      <td><span className={`badge ${priorityColor(t.priority)}`}>{t.priority}</span></td>
                      <td style={{ color: t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'DONE' ? '#dc2626' : '#6b7280', fontSize: 13, fontWeight: t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'DONE' ? 600 : 400 }}>{t.dueDate || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-ghost" onClick={() => navigate(`/tasks/${t.id}`)} style={{ padding: '4px 8px', fontSize: 12 }}>View</button>
                          {(isAdmin() || t.createdBy === currentUser.username) && <button className="btn-ghost" onClick={() => deleteTask(t.id)} style={{ padding: '4px 8px', fontSize: 12, color: '#dc2626' }}>Delete</button>}
                        </div>
                      </td>
                    </tr>
                    {childTasks.map(s => (
                      <tr key={s.id} style={{ background: '#f9fafb' }}>
                        <td style={{ fontSize: 11, color: '#9ca3af', paddingLeft: 24 }}>↳ {s.taskCode}</td>
                        <td style={{ fontSize: 12, color: '#6b7280', paddingLeft: 24 }}>{s.title}</td>
                        <td>{s.assigneeName ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}><Avatar name={s.assigneeName} size={18} fontSize={8} /> {s.assigneeName}</span> : <span style={{ color: '#ccc' }}>—</span>}</td>
                        <td></td>
                        <td><span className={`badge ${statusColor(s.status)}`} style={{ fontSize: 10 }}>{s.status.replace(/_/g, ' ')}</span></td>
                        <td><span className={`badge ${priorityColor(s.priority)}`} style={{ fontSize: 10 }}>{s.priority}</span></td>
                        <td style={{ fontSize: 12, color: '#6b7280' }}>{s.dueDate || '—'}</td>
                        <td>
                          <button className="btn-ghost" onClick={() => navigate(`/tasks/${s.id}`)} style={{ padding: '2px 6px', fontSize: 11 }}>View</button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* MILESTONES TAB */}
      {tab === 'milestones' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Milestones ({milestones.length})</h3>
            {isManager() && <button className="btn-primary" onClick={() => { setShowMsForm(true); setEditingMs(null); setMsForm(emptyMsForm); }} style={{ fontSize: 13 }}>+ Add Milestone</button>}
          </div>
          {milestones.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>No milestones yet</div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {milestones.map(m => {
                const stats = getMsStats(m.id);
                const isOverdue = new Date(m.dueDate) < new Date() && m.status !== 'COMPLETED';
                const msTasks = tasks.filter(t => t.milestoneId === m.id);
                return (
                  <div key={m.id} className="card" style={{ padding: 20, borderLeft: `4px solid ${isOverdue ? '#dc2626' : m.status === 'COMPLETED' ? '#059669' : '#3b82f6'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>🎯 {m.milestoneName}</div>
                        {m.description && <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{m.description}</div>}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className={`badge ${statusColor(m.status)}`}>{m.status}</span>
                          <span style={{ fontSize: 12, color: isOverdue ? '#dc2626' : '#6b7280', fontWeight: isOverdue ? 600 : 400 }}>📅 {m.dueDate}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {isManager() && (
                          <>
                            <button className="btn-ghost" onClick={() => { setEditingMs(m.id); setMsForm({ milestoneName: m.milestoneName, startDate: m.startDate || '', dueDate: m.dueDate, status: m.status, description: m.description || '' }); setShowMsForm(true); }} style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                            {(isAdmin() || m.createdBy === currentUser.username) && (
                              <button className="btn-ghost" onClick={() => deleteMs(m.id)} style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626' }}>Delete</button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Progress */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                        <span style={{ color: '#6b7280' }}>{stats.done} of {stats.total} tasks completed</span>
                        <span style={{ fontWeight: 600, color: stats.pct === 100 ? '#059669' : '#374151' }}>{stats.pct}%</span>
                      </div>
                      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: `${stats.pct}%`, background: stats.pct === 100 ? '#059669' : '#3b82f6', borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                    </div>

                    {/* Linked Tasks */}
                    {msTasks.length > 0 && (
                      <div style={{ background: '#f9fafb', borderRadius: 8, padding: 10 }}>
                        {msTasks.map(t => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, minWidth: 45 }}>{t.taskCode}</span>
                            <span style={{ flex: 1, color: '#374151' }}>{t.title}</span>
                            <span className={`badge ${statusColor(t.status)}`} style={{ fontSize: 10 }}>{t.status.replace(/_/g, ' ')}</span>
                            {t.assigneeName && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#6b7280' }}><Avatar name={t.assigneeName} size={18} fontSize={8} /> {t.assigneeName}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {stats.total === 0 && (
                      <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No tasks linked to this milestone</div>
                    )}

                    {/* Milestone Attachments */}
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>📎 Documents ({(msAttachments[m.id] || []).length})</span>
                        {isManager() && (
                          <label style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                            {msUploading === m.id ? 'Uploading...' : '+ Upload'}
                            <input type="file" style={{ display: 'none' }} onChange={e => uploadMsFile(m.id, e.target.files[0])} disabled={msUploading === m.id} />
                          </label>
                        )}
                      </div>
                      {(msAttachments[m.id] || []).length > 0 && (
                        <div style={{ display: 'grid', gap: 4 }}>
                          {(msAttachments[m.id] || []).map(att => (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #f0f0f0', fontSize: 12 }}>
                              <span>{getFileIcon(att.fileName)}</span>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>{att.fileName}</span>
                              <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{formatFileSize(att.fileSize)}</span>
                              <button className="btn-ghost" onClick={() => downloadAttachment(att)} style={{ padding: '2px 6px', fontSize: 10, color: '#3b82f6' }}>View</button>
                              {(isAdmin() || att.createdBy === currentUser.username) && (
                                <button className="btn-ghost" onClick={() => deleteAttachment(att, m.id)} style={{ padding: '2px 6px', fontSize: 10, color: '#dc2626' }}>Del</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TIMELINE TAB */}
      {tab === 'timeline' && (
        <div className="card" style={{ padding: 20 }}>
          <GanttChart
            tasks={tasks.filter(t => !t.parentTaskId)}
            milestones={milestones}
            project={project}
            onTaskClick={(taskId) => navigate(`/tasks/${taskId}`)}
          />
        </div>
      )}

      {/* DOCUMENTS TAB */}
      {tab === 'documents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>All Documents ({projectAttachments.length})</h3>
          </div>
          {projectAttachments.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>No documents uploaded yet. Upload files from Task Detail or Milestones tab.</div>
          ) : (
            <div className="card">
              <table>
                <thead><tr><th></th><th>File Name</th><th>Type</th><th>Linked To</th><th>Size</th><th>Uploaded By</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {projectAttachments.map(att => {
                    const linkedTask = att.entityType === 'TASK' ? tasks.find(t => t.id === att.entityId) : null;
                    const linkedMs = att.entityType === 'MILESTONE' ? milestones.find(m => m.id === att.entityId) : null;
                    return (
                      <tr key={att.id}>
                        <td style={{ fontSize: 18, width: 30 }}>{getFileIcon(att.fileName)}</td>
                        <td style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.fileName}</td>
                        <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{att.entityType}</span></td>
                        <td style={{ fontSize: 12, color: '#3b82f6', cursor: 'pointer' }} onClick={() => {
                          if (linkedTask) navigate(`/tasks/${linkedTask.id}`);
                        }}>
                          {linkedTask ? `${linkedTask.taskCode} - ${linkedTask.title}` : linkedMs ? `🎯 ${linkedMs.milestoneName}` : att.entityType + ' #' + att.entityId}
                        </td>
                        <td style={{ fontSize: 12, color: '#6b7280' }}>{formatFileSize(att.fileSize)}</td>
                        <td style={{ fontSize: 12, color: '#6b7280' }}>{att.createdBy}</td>
                        <td style={{ fontSize: 12, color: '#6b7280' }}>{new Date(att.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn-ghost" onClick={() => downloadAttachment(att)} style={{ padding: '3px 8px', fontSize: 11, color: '#3b82f6' }}>View</button>
                            {(isAdmin() || att.createdBy === currentUser.username) && (
                              <button className="btn-ghost" onClick={async () => { await deleteAttachment(att); setProjectAttachments(prev => prev.filter(a => a.id !== att.id)); }} style={{ padding: '3px 8px', fontSize: 11, color: '#dc2626' }}>Delete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MEMBERS TAB */}
      {tab === 'members' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Project Members ({activeProjectMembers.length})</h3>
            {isManager() && <button className="btn-primary" onClick={() => setShowAddMember(true)} style={{ fontSize: 13 }}>+ Add Member</button>}
          </div>

          {/* Add Member Form */}
          {showAddMember && (
            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Add Member to Project</h4>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>User *</label>
                  <select value={addMemberForm.userId} onChange={e => setAddMemberForm({ ...addMemberForm, userId: e.target.value })}>
                    <option value="">Select User</option>
                    {users.filter(u => u.active && !existingMemberUserIds.includes(u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>
                    ))}
                  </select>
                </div>
                <div style={{ width: 160 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Team</label>
                  <select value={addMemberForm.teamId} onChange={e => setAddMemberForm({ ...addMemberForm, teamId: e.target.value })}>
                    <option value="">No Team</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
                  </select>
                </div>
                <div style={{ width: 140 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
                  <select value={addMemberForm.roleInProject} onChange={e => setAddMemberForm({ ...addMemberForm, roleInProject: e.target.value })}>
                    {MEMBER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <button className="btn-primary" onClick={addProjectMember} style={{ padding: '8px 16px', fontSize: 13 }}>Add</button>
                <button className="btn-ghost" onClick={() => setShowAddMember(false)} style={{ padding: '8px 12px', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Members List */}
          {activeProjectMembers.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>No members added yet. Add team members to assign tasks.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {activeProjectMembers.map(m => {
                const memberTasks = tasks.filter(t => t.assigneeId === m.userId);
                const doneTasks = memberTasks.filter(t => t.status === 'DONE').length;
                return (
                  <div key={m.id} className="card" style={{ padding: '14px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={m.fullName} size={40} fontSize={16} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{m.fullName}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{m.username}{m.teamName ? ` · ${m.teamName}` : ''}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: m.roleInProject === 'TEAM_LEAD' ? '#fef3c7' : m.roleInProject === 'DEVELOPER' ? '#dbeafe' : '#f3f4f6', color: m.roleInProject === 'TEAM_LEAD' ? '#92400e' : m.roleInProject === 'DEVELOPER' ? '#1d4ed8' : '#374151' }}>
                        {m.roleInProject}
                      </span>
                      <div style={{ textAlign: 'center', minWidth: 60 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{memberTasks.length}</div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>tasks</div>
                      </div>
                      {memberTasks.length > 0 && (
                        <div style={{ width: 60, textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>{doneTasks}/{memberTasks.length}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>done</div>
                        </div>
                      )}
                      {(isAdmin() || m.createdBy === currentUser.username) && (
                        <button className="btn-ghost" onClick={() => removeProjectMember(m.id)} style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626' }}>Remove</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
